/**
 * Bandeja de confirmaciones — flujo de NIVEL 2.
 *
 * Contrato de seguridad (especificacion, secciones 3.3 y 7):
 *  - Estas propuestas ya existen en la tabla `confirmaciones`; el kiosco NO las
 *    crea ni ejecuta la herramienta. Solo resuelve: confirmar o rechazar.
 *  - El backend valida el nivel y el token efimero. Si el TTL expiro devuelve
 *    403 y la propuesta se marca caducada. La seguridad NO depende de esta UI.
 *  - La via legitima es el "si" verbal en sesion activa. Estos botones son la
 *    via secundaria y el backend los registra como `via: kiosco_tactil` en
 *    `ai_decision_log`.
 *
 * Diseno: se muestra literalmente `texto_leido_al_jefe` —la misma frase que
 * Androcles pronuncia— para que oido y vista digan exactamente lo mismo.
 * Los botones Si/No son enormes, separados y de color distinto: no debe haber
 * ninguna posibilidad de confirmar por error.
 */
import { useState } from 'react';
import { Boton } from '@/components/ui/button';
import { Tarjeta, TarjetaCabecera, TarjetaTitulo, TarjetaContenido, TarjetaPie } from '@/components/ui/card';
import { ErrorApi, type Confirmacion } from '@/lib/api';
import { formatearHora, cn } from '@/lib/utils';

interface PropsBandeja {
  confirmaciones: Confirmacion[];
  cargando?: boolean;
  /** Resuelve en el backend. Debe refrescar la lista al terminar. */
  alResolver: (id: number, decision: 'confirmada' | 'rechazada') => Promise<void>;
  className?: string;
}

export function BandejaConfirmaciones({
  confirmaciones,
  cargando = false,
  alResolver,
  className,
}: PropsBandeja) {
  const pendientes = confirmaciones.filter((c) => c.estado === 'pendiente');

  return (
    <section className={cn('flex flex-col gap-5', className)} aria-label="Pendiente de su visto bueno">
      <header className="flex items-baseline justify-between gap-5">
        <h2 className="text-2xl font-bold">Pendiente de su visto bueno</h2>
        {pendientes.length > 0 && (
          <span className="rounded-full bg-[hsl(var(--alerta))] px-5 py-1 text-lg font-bold text-background">
            {pendientes.length}
          </span>
        )}
      </header>

      {cargando && <p className="text-lg text-muted-foreground">Mirando la bandeja…</p>}

      {!cargando && pendientes.length === 0 && (
        <p className="rounded-lg border-2 border-dashed border-border p-8 text-center text-lg text-muted-foreground">
          No hay nada esperando su decision.
        </p>
      )}

      {pendientes.map((confirmacion) => (
        <FichaConfirmacion
          key={confirmacion.id}
          confirmacion={confirmacion}
          alResolver={alResolver}
        />
      ))}
    </section>
  );
}

/** Ficha de una propuesta con sus dos unicas salidas: si o no. */
function FichaConfirmacion({
  confirmacion,
  alResolver,
}: {
  confirmacion: Confirmacion;
  alResolver: (id: number, decision: 'confirmada' | 'rechazada') => Promise<void>;
}) {
  const [enviando, setEnviando] = useState<'confirmada' | 'rechazada' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detalleAbierto, setDetalleAbierto] = useState(false);

  const resolver = async (decision: 'confirmada' | 'rechazada') => {
    setEnviando(decision);
    setError(null);
    try {
      await alResolver(confirmacion.id, decision);
    } catch (e) {
      // El 403 no es un fallo tecnico: es el TTL de Nivel 2 haciendo su trabajo.
      setError(
        e instanceof ErrorApi && e.esConfirmacionRequerida
          ? 'Ha pasado demasiado tiempo. Pidamelo otra vez y se lo vuelvo a plantear.'
          : e instanceof Error
            ? e.message
            : 'No he podido registrar su respuesta.',
      );
    } finally {
      setEnviando(null);
    }
  };

  const caducada = !confirmacion.vigente;

  return (
    <Tarjeta className={cn('animate-entrada-suave', caducada && 'opacity-60')}>
      <TarjetaCabecera>
        <TarjetaTitulo>Le propongo un cambio</TarjetaTitulo>
        <p className="text-sm text-muted-foreground">
          {confirmacion.herramienta} · propuesto a las {formatearHora(confirmacion.ts_propuesta)}
        </p>
      </TarjetaCabecera>

      <TarjetaContenido className="flex flex-col gap-5">
        {/* Exactamente lo que Androcles lee en voz alta. Texto plano. */}
        <p className="whitespace-pre-wrap text-lg leading-relaxed">
          {confirmacion.texto_leido_al_jefe}
        </p>

        <details
          open={detalleAbierto}
          onToggle={(e) => setDetalleAbierto((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary className="min-h-tactil cursor-pointer list-none py-4 text-base font-semibold text-muted-foreground underline">
            {detalleAbierto ? 'Ocultar el detalle tecnico' : 'Ver el detalle tecnico'}
          </summary>
          {/* Auditoria: el jefe es auditor y quiere poder mirar la letra pequena. */}
          <pre className="mt-3 max-h-72 overflow-auto rounded-lg bg-muted p-5 text-sm">
            {JSON.stringify(confirmacion.propuesta, null, 2)}
          </pre>
        </details>

        {caducada && (
          <p className="text-base font-semibold text-[hsl(var(--alerta))]">
            Esta propuesta ha caducado; tendria que plantearsela de nuevo.
          </p>
        )}

        {error && (
          <p role="alert" className="text-base font-semibold text-destructive">
            {error}
          </p>
        )}
      </TarjetaContenido>

      <TarjetaPie className="flex-col gap-4 sm:flex-row">
        <Boton
          variante="afirmativo"
          tamano="grande"
          className="flex-1"
          disabled={enviando !== null || caducada}
          onClick={() => resolver('confirmada')}
        >
          {enviando === 'confirmada' ? 'Registrando…' : 'Si, adelante'}
        </Boton>
        <Boton
          variante="contorno"
          tamano="grande"
          className="flex-1"
          disabled={enviando !== null || caducada}
          onClick={() => resolver('rechazada')}
        >
          {enviando === 'rechazada' ? 'Registrando…' : 'No, dejalo'}
        </Boton>
      </TarjetaPie>
    </Tarjeta>
  );
}
